import { Plus, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AccessEditor } from "@/features/form-access";
import { ApiError } from "@/lib/api";
import {
  FIELD_TITLE_MAX, FIELD_TYPES, LONG_TEXT_MAX_LENGTH, NEW_FIELD, OPTIONS_MAX, RESERVED_FIELD_KEYS,
  TEXT_MAX_LENGTH,
  blankOption, displayChoices, entityLabel, fieldChanges, fieldKeys, fieldProblems, fieldTypeText, fieldValues,
  filledNote, folderLevels, hasOptions, hasProblems, keyFromTitle, newFieldPayload, newFieldValues,
  nextOptionId, recordNoun, sectionChoices, typeChangeProblem, withOptionKey, withOptionLabel,
  withoutOption, type FieldProblems, type FieldValues, type OptionDraft,
} from "@/lib/forms";
import { useCreateFormField, useUpdateFormField } from "@/lib/queries";
import type { AdminForm, AdminFormField, FormEntity, FormFieldType } from "@/lib/types";
import { cn } from "@/lib/utils";

/** The "Shown as" choice that keeps the record's own name; a Select cannot hold an empty value. */
const NAME = "__name__";

/** What every control says that a platform field cannot change. */
const PLATFORM_FIELD = "One of the platform's own fields.";

/**
 * Add a custom field to a contact or a firm, or edit one (ACL-256): what it is called, what it accepts,
 * which folder it is in, and who may see and edit it.
 *
 * The name a field is stored by is typed once, on create, and suggested from its title: every record's
 * value, an import and any other system name the field by it, so it is not renamed afterwards; the
 * title is. What the type takes (a text's length, a number's range, a choice's options, what a
 * reference points at) is set here too. A field that records already hold a value in keeps its type
 * unless the change leaves every value as it is (`typeChangeProblem`), which the dialog says before
 * the save rather than after the server's refusal. One of the platform's own fields takes a new title,
 * section and who sees it only.
 *
 * Moving a field to another section is the Section picker: the server puts it last there. Its order
 * within a section is the arrows on the page, which save at once.
 */
export function FormFieldDialog({
  open, onOpenChange, field, sectionKey, form, entity, tenantId, forms,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  field?: AdminFormField;
  /** The section the field is in, or the one "New field" was pressed in. */
  sectionKey?: string;
  /** The design: the sections to pick from, and the stored names already taken. */
  form: AdminForm | undefined;
  entity: FormEntity;
  /** A superadmin's tenant; null for everyone else, whose own it is. */
  tenantId: number | null;
  /** Both objects' designs, for what a reference can be shown by: the target form's fields. */
  forms?: Partial<Record<FormEntity, AdminForm | undefined>>;
}) {
  const toast = useToast();
  const create = useCreateFormField();
  const update = useUpdateFormField();
  const [values, setValues] = React.useState<FieldValues>(NEW_FIELD);
  const [initial, setInitial] = React.useState<FieldValues>(NEW_FIELD);
  /** Whether the stored name still follows the title: until somebody types in it. */
  const [keyFollows, setKeyFollows] = React.useState(true);
  const [tried, setTried] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const creating = !field;
  const system = Boolean(field?.system);
  const noun = recordNoun(entity);
  const taken = React.useMemo(() => fieldKeys(form), [form]);

  React.useEffect(() => {
    if (!open) return;
    const firstSection = form?.sections.find((s) => !s.archived)?.key ?? "";
    const start = field ? fieldValues(field, sectionKey ?? "") : newFieldValues(sectionKey ?? firstSection);
    setValues(start);
    setInitial(start);
    setKeyFollows(!field);
    setTried(false);
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, field, sectionKey]);

  const set = <K extends keyof FieldValues>(name: K, value: FieldValues[K]) =>
    setValues((v) => {
      const next = { ...v, [name]: value };
      if (name === "title" && keyFollows && creating) next.key = keyFromTitle(String(value), taken, RESERVED_FIELD_KEYS);
      // What a reference is shown by is a field of the form it points to: a new target starts over.
      if (name === "target" && value !== v.target) next.display = "";
      // A choice needs an option to be saved: start the list with a row to type in.
      if (name === "type" && hasOptions(String(value)) && next.options.length === 0) {
        next.options = [blankOption(nextOptionId(next.options))];
      }
      return next;
    });

  const problems = fieldProblems(values, taken, creating, system);
  const typeProblem = field ? typeChangeProblem(field, values.type, noun) : null;
  const changed = JSON.stringify(values) !== JSON.stringify(initial);
  const pending = create.isPending || update.isPending;
  const choices = sectionChoices(form, initial.section);
  const folder = folderLevels(form?.sections.find((s) => s.key === values.section)?.rules);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    setFormError(null);
    if (hasProblems(problems) || typeProblem) return;
    try {
      if (field) {
        const changes = fieldChanges(initial, values, system);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ entity, tenantId, key: field.key, ...changes });
        }
        toast.success("Field saved", values.title.trim());
      } else {
        await create.mutateAsync({ entity, tenantId, ...newFieldPayload(values) });
        toast.success("Field added", values.title.trim());
      }
      onOpenChange(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save the field.");
    }
  };

  const shown = (name: Exclude<keyof FieldProblems, "optionRows">) => (tried ? problems[name] : undefined);
  // The stored name is checked as it is typed: it is the one field that cannot be fixed later.
  const keyError = creating && (values.key || tried) ? problems.key : undefined;
  const isReference = values.type === "reference";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{field ? "Edit field" : `New ${noun} field`}</DialogTitle>
          <DialogDescription>
            {field
              ? system
                ? "One of the platform's own fields: its name, folder and who can see it can change, not what it is."
                : "What it is called, what it accepts, and who can see and edit it. Its key is fixed."
              : `A field on every ${noun}. It goes last in its folder, and answers are kept under its key.`}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={(e) => void submit(e)} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Field name"
                htmlFor="form-field-title"
                required
                error={shown("title")}
                hint={`What people see. At most ${FIELD_TITLE_MAX} characters.`}
              >
                <Input
                  autoComplete="off"
                  placeholder="Accident Date"
                  invalid={Boolean(shown("title"))}
                  value={values.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("title", e.target.value)}
                />
              </Field>
              <Field
                label="Key"
                htmlFor="form-field-key"
                required={creating}
                error={keyError}
                hint={
                  creating
                    ? `The ${noun}'s fields are saved and read by this key. It cannot change later, even if the name does.`
                    : `Fixed: ${values.key}.`
                }
              >
                <Input
                  // Read-only rather than disabled, as on the other catalogs' forms.
                  readOnly={!creating}
                  className={cn("font-mono", !creating && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="accident_date"
                  invalid={Boolean(keyError)}
                  value={values.key}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setKeyFollows(false);
                    set("key", e.target.value);
                  }}
                />
              </Field>
            </div>

            <Field
              label="Type"
              htmlFor="form-field-type"
              hint={
                system
                  ? PLATFORM_FIELD
                  : (field && filledNote(field.values, noun)) || fieldTypeText(values.type).description
              }
            >
              <Select
                value={values.type}
                disabled={system}
                onValueChange={(v) => set("type", v as FormFieldType)}
              >
                <SelectTrigger id="form-field-type" className="sm:w-72">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type} value={type} description={fieldTypeText(type).description}>
                      {fieldTypeText(type).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {typeProblem && <Callout tone="warning">{typeProblem}</Callout>}

            <Field
              label="Folder"
              htmlFor="form-field-section"
              required
              error={shown("section")}
              hint={
                creating
                  ? "It goes last in the folder you pick."
                  : "Picking another folder moves the field to the end of it."
              }
            >
              <Select value={values.section} onValueChange={(v) => set("section", v)}>
                <SelectTrigger id="form-field-section" className="sm:w-72">
                  <SelectValue placeholder="Pick a folder" />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.title}
                      {s.archived ? " (archived)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
              <div>
                <p id="form-field-required-label" className="text-xs font-medium text-text">
                  Required
                </p>
                <p className="text-xs text-text-muted">
                  {system
                    ? PLATFORM_FIELD
                    : `Once a ${noun} has a value in it, the value cannot be cleared. A ${noun} with none today stays readable and editable.`}
                </p>
              </div>
              <Switch
                checked={values.required}
                disabled={system}
                onCheckedChange={(checked) => set("required", checked)}
                aria-labelledby="form-field-required-label"
              />
            </div>
            {shown("required") && (
              <p role="alert" className="text-xs text-danger-11">
                {shown("required")}
              </p>
            )}

            <AccessEditor
              kind="field"
              idPrefix="form-field-access"
              value={values.access}
              folder={folder}
              platform={system}
              onChange={(access) => set("access", access)}
            />

            {(values.type === "text" || values.type === "long_text") && (
              <Field
                label="Longest answer"
                htmlFor="form-field-max-length"
                error={shown("max_length")}
                hint={
                  system
                    ? PLATFORM_FIELD
                    : `Characters. Leave it empty for the most a ${values.type === "text" ? "Text" : "Long Text"} takes: ${(values.type === "text" ? TEXT_MAX_LENGTH : LONG_TEXT_MAX_LENGTH).toLocaleString("en-US")}.`
                }
              >
                <Input
                  inputMode="numeric"
                  autoComplete="off"
                  className="sm:w-40"
                  disabled={system}
                  invalid={Boolean(shown("max_length"))}
                  value={values.max_length}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("max_length", e.target.value)}
                />
              </Field>
            )}

            {values.type === "number" && (
              <div className="space-y-1.5">
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Smallest" htmlFor="form-field-min" error={shown("min")}>
                    <Input
                      inputMode="decimal"
                      autoComplete="off"
                      disabled={system}
                      invalid={Boolean(shown("min"))}
                      value={values.min}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("min", e.target.value)}
                    />
                  </Field>
                  <Field label="Largest" htmlFor="form-field-max" error={shown("max")}>
                    <Input
                      inputMode="decimal"
                      autoComplete="off"
                      disabled={system}
                      invalid={Boolean(shown("max"))}
                      value={values.max}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("max", e.target.value)}
                    />
                  </Field>
                  <Field label="Decimal places" htmlFor="form-field-decimals" error={shown("decimals")}>
                    <Input
                      inputMode="numeric"
                      autoComplete="off"
                      disabled={system}
                      invalid={Boolean(shown("decimals"))}
                      value={values.decimals}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("decimals", e.target.value)}
                    />
                  </Field>
                </div>
                <p className="text-xs text-text-muted">
                  {system
                    ? PLATFORM_FIELD
                    : "Each is optional. Decimal places are 0 to 6; leave it empty for up to 6."}
                </p>
              </div>
            )}

            {hasOptions(values.type) && (
              <OptionsEditor
                options={values.options}
                onChange={(options) => set("options", options)}
                disabled={system}
                error={shown("options")}
                rowErrors={tried ? problems.optionRows : undefined}
              />
            )}

            {isReference && (
              <Field
                label="Points to"
                htmlFor="form-field-target"
                required
                error={shown("target")}
                hint={
                  system
                    ? PLATFORM_FIELD
                    : "People pick the record from a search. What is kept is the record itself, so renaming it never breaks the link."
                }
              >
                <Select
                  value={values.target}
                  disabled={system}
                  onValueChange={(v) => set("target", v as FieldValues["target"])}
                >
                  <SelectTrigger id="form-field-target" className="sm:w-60">
                    <SelectValue placeholder="Pick one" />
                  </SelectTrigger>
                  <SelectContent>
                    {(["lead", "firm"] as const).map((target) => (
                      <SelectItem key={target} value={target}>
                        {entityLabel(target)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {isReference && values.target && (
              <Field
                label="Shown as"
                htmlFor="form-field-display"
                hint={`What the record shows for the linked ${recordNoun(values.target)}: its name, or one of its own fields.`}
              >
                <Select
                  value={values.display || NAME}
                  disabled={system}
                  onValueChange={(v) => set("display", v === NAME ? "" : v)}
                >
                  <SelectTrigger id="form-field-display" className="sm:w-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NAME}>Its name</SelectItem>
                    {displayChoices(forms?.[values.target]).map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {f.title}
                      </SelectItem>
                    ))}
                    {/* A display field archived since keeps showing what it is, rather than a blank. */}
                    {values.display && !displayChoices(forms?.[values.target]).some((f) => f.key === values.display) && (
                      <SelectItem value={values.display}>{values.display}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={pending} disabled={Boolean(typeProblem)}>
              {field ? "Save changes" : "Add field"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The options of a dropdown or a multi-select: a name on each row, and under it the name it is stored
 * as, which follows the name until somebody types in it. A saved option's stored name is fixed, because
 * records keep it and not the label, and a saved option can be removed here too: the server refuses
 * when records use it, and its message is shown with the rest of the save's.
 */
function OptionsEditor({
  options, onChange, disabled, error, rowErrors,
}: {
  options: OptionDraft[];
  onChange: (options: OptionDraft[]) => void;
  disabled: boolean;
  /** The list as a whole: empty, or too long. */
  error?: string;
  /** What is wrong with a row, by its position. */
  rowErrors?: Record<number, string>;
}) {
  /** The row just added, which takes the cursor. */
  const [focusId, setFocusId] = React.useState<string | null>(null);

  const add = () => {
    const id = nextOptionId(options);
    setFocusId(id);
    onChange([...options, blankOption(id)]);
  };

  // Enter in a list of options starts the next one, instead of saving the field half written.
  const onEnter = (e: React.KeyboardEvent, row: OptionDraft) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (row.label.trim() && options.length < OPTIONS_MAX) add();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <p id="form-field-options-label" className="flex items-center gap-1 text-xs font-medium text-text">
          Options
          <span className="text-danger-9" aria-hidden>
            *
          </span>
        </p>
        <p className="text-xs text-text-muted">
          {options.length} of {OPTIONS_MAX}
        </p>
      </div>

      {options.length > 0 && (
        <ul aria-labelledby="form-field-options-label" className="space-y-2">
          {options.map((option, i) => (
            <li key={option.id} className="space-y-1">
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start">
                <Input
                  autoComplete="off"
                  aria-label={`Option ${i + 1} name`}
                  placeholder="What the rep picks"
                  className="sm:flex-1"
                  autoFocus={option.id === focusId}
                  disabled={disabled}
                  invalid={Boolean(rowErrors?.[i])}
                  value={option.label}
                  onKeyDown={(e) => onEnter(e, option)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(withOptionLabel(options, i, e.target.value))
                  }
                />
                <div className="flex items-start gap-1.5">
                  <Input
                    // Read-only once saved, as a stage's stored name is.
                    readOnly={option.saved}
                    autoComplete="off"
                    aria-label={`Option ${i + 1} stored as`}
                    placeholder="stored_as"
                    className={cn(
                      "min-w-0 flex-1 font-mono sm:w-40 sm:flex-none",
                      option.saved && "bg-surface-sunken text-text-muted",
                    )}
                    disabled={disabled}
                    invalid={Boolean(rowErrors?.[i])}
                    value={option.key}
                    onKeyDown={(e) => onEnter(e, option)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(withOptionKey(options, i, e.target.value))
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove option ${i + 1}`}
                    disabled={disabled}
                    onClick={() => onChange(withoutOption(options, i))}
                  >
                    <X />
                  </Button>
                </div>
              </div>
              {rowErrors?.[i] && (
                <p role="alert" className="text-xs text-danger-11">
                  {rowErrors[i]}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="text-xs text-danger-11">
          {error}
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || options.length >= OPTIONS_MAX}
        onClick={add}
      >
        <Plus />
        Add option
      </Button>
      <p className="text-xs text-text-muted">
        {disabled
          ? PLATFORM_FIELD
          : "A name can be changed later. The stored name cannot once the option is saved, because records keep it. An option that records use cannot be removed."}
      </p>
    </div>
  );
}
